import React, { forwardRef, type ComponentType } from "react";
import { Link } from "react-router-dom";
import { twJoin, twMerge } from "tailwind-merge";
import { LoaderIcon } from "@components/icons";

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "link";

export interface IButton {
  /**
   * Button variant. Default: primary
   */
  variant?: ButtonVariant;
  /**
   * Icon element.
   */
  icon?: ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Icon class name.
   */
  iconClassName?: string;
  /**
   * Button loading state flag.
   */
  isLoading?: boolean;
  /**
   * Disable text truncation if we need to force full text.
   */
  disableTruncate?: boolean;
}

export interface IButtonProps
  extends IButton,
    React.ButtonHTMLAttributes<HTMLButtonElement> {}

export interface IButtonLinkProps
  extends IButton,
    React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * Anchor link.
   */
  href?: string;
}

const getButtonClassName = (
  variant: ButtonVariant,
  className: string | undefined,
  isChildren: boolean,
  disableTruncate?: boolean,
) =>
  variant === "link"
    ? twMerge(
        "text-sm font-semibold hover:underline active:font-bold cursor-pointer flex items-center gap-2",
        className,
      )
    : twMerge(
        "group block h-10 cursor-pointer rounded-full px-4 py-2.5 text-sm font-semibold outline-none transition-colors focus:ring-4 active:ring-4 active:ring-opacity-10 focus:ring-opacity-10 flex items-center justify-center gap-2 select-none w-fit disabled:ring-0 disabled:cursor-default",
        !disableTruncate && "truncate",
        !isChildren && (variant === "secondary" ? "px-[9px]" : "px-2.5"),
        variant === "primary" &&
          "bg-base-black text-base-white hover:bg-base-light-black active:bg-base-black focus:ring-base-black active:ring-base-black disabled:bg-grey-medium disabled:text-base-white",
        variant === "secondary" &&
          "bg-base-white border border-black hover:bg-blue-hover text-base-black active:bg-blue-light focus:ring-blue-light active:ring-blue-light focus:ring-opacity-50 active:ring-opacity-50 disabled:border-grey-medium disabled:text-base-grey disabled:hover:bg-base-white disabled:text-opacity-50 disabled:border-opacity-30",
        variant === "tertiary" &&
          "bg-base-white hover:bg-[#D7E3F2] text-blue-dark active:bg-blue-light focus:ring-blue-light active:ring-blue-light focus:ring-opacity-50 active:ring-opacity-50 disabled:hover:bg-base-white disabled:text-opacity-50 disabled:border-opacity-30",
        className,
      );

const getIconClassName = (variant: ButtonVariant, className?: string) =>
  twMerge(
    "h-5 w-5 flex-shrink-0",
    variant === "primary" && "text-base-white group-disabled:text-base-white",
    variant === "secondary" &&
      "text-blue-dark group-disabled:text-opacity-50 group-disabled:text-base-grey",
    className,
  );

const wrapChildren = (children: React.ReactNode, disableTruncate?: boolean) =>
  children && (
    <span className={twJoin(!disableTruncate && "truncate")}>{children}</span>
  );

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      className,
      iconClassName,
      variant = "primary",
      children,
      icon,
      isLoading,
      disableTruncate,
      ...rest
    },
    ref,
  ) => {
    return React.createElement(
      "button",
      {
        className: getButtonClassName(
          variant,
          className,
          !!children,
          disableTruncate,
        ),
        ref,
        ...rest,
        ...(isLoading
          ? {
              disabled: true,
            }
          : {}),
      },
      icon && variant !== "link" && !isLoading
        ? React.createElement(icon, {
            className: getIconClassName(variant, iconClassName),
          })
        : null,
      isLoading
        ? React.createElement(LoaderIcon, {
            className: twMerge(getIconClassName(variant), "animate-spin"),
          })
        : null,
      wrapChildren(children, disableTruncate),
    );
  },
);

export const ButtonLink = forwardRef<HTMLAnchorElement, IButtonLinkProps>(
  (
    {
      className,
      variant = "primary",
      children,
      icon,
      isLoading,
      href,
      ...rest
    },
    ref,
  ) => {
    return React.createElement(
      href ? Link : "a",
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      {
        className: getButtonClassName(variant, className, !!children),
        ...(href ? { to: href } : {}),
        ref,
        ...rest,
        ...(isLoading
          ? {
              disabled: true,
            }
          : {}),
      },
      icon && variant !== "link" && !isLoading
        ? React.createElement(icon, {
            className: getIconClassName(variant),
          })
        : null,
      isLoading
        ? React.createElement(LoaderIcon, {
            className: twMerge(getIconClassName(variant), "animate-spin"),
          })
        : null,
      wrapChildren(children),
    );
  },
);
